import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Plane,
  Search,
  Download,
  RefreshCw,
  Eye,
  Calendar,
  User,
  Mail,
  Phone,
  MapPin,
  Clock,
  CreditCard,
  ChevronDown,
  ChevronUp,
  Filter,
  X,
  FileText,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { downloadETicketPDF } from '../../utils/pdfGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BookingsPanel = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [dateFilter, setDateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [generatingPDF, setGeneratingPDF] = useState(null);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/bookings?limit=100`);
      if (response.data.success) {
        setBookings(response.data.bookings || []);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      booking.pnr?.toLowerCase().includes(searchLower) ||
      booking.contact_email?.toLowerCase().includes(searchLower) ||
      booking.passengers?.some(p => 
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchLower)
      ) ||
      booking.flight_data?.airline?.toLowerCase().includes(searchLower);
    
    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all') {
      const bookingDate = new Date(booking.created_at);
      const today = new Date();
      if (dateFilter === 'today') {
        matchesDate = bookingDate.toDateString() === today.toDateString();
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today.setDate(today.getDate() - 7));
        matchesDate = bookingDate >= weekAgo;
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(today.setMonth(today.getMonth() - 1));
        matchesDate = bookingDate >= monthAgo;
      }
    }

    return matchesSearch && matchesDate;
  });

  const exportToCSV = () => {
    const headers = ['PNR', 'Passenger', 'Email', 'Phone', 'Route', 'Airline', 'Date', 'Price', 'Status', 'Created'];
    const rows = filteredBookings.map(b => [
      b.pnr,
      b.passengers?.map(p => `${p.first_name} ${p.last_name}`).join('; ') || '',
      b.contact_email || '',
      b.contact_phone || '',
      `${b.flight_data?.from || ''} → ${b.flight_data?.to || ''}`,
      b.flight_data?.airline || '',
      b.flight_data?.departure_date || '',
      `£${b.flight_data?.price || 0}`,
      b.status || 'confirmed',
      new Date(b.created_at).toLocaleDateString()
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    return timeStr.split('+')[0].substring(0, 5);
  };

  // Use centralized PDF utility for booking PDF generation
  const generateBookingPDF = async (booking) => {
    setGeneratingPDF(booking.id);
    try {
      await downloadETicketPDF(booking);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPDF(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Bookings</p>
                <p className="text-3xl font-bold">{bookings.length}</p>
              </div>
              <Plane className="h-10 w-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Confirmed</p>
                <p className="text-3xl font-bold">
                  {bookings.filter(b => b.status !== 'cancelled').length}
                </p>
              </div>
              <Badge className="bg-white/20 text-white">Active</Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold">
                  £{bookings.reduce((sum, b) => sum + (b.flight_data?.price || 0), 0).toLocaleString()}
                </p>
              </div>
              <CreditCard className="h-10 w-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Today&apos;s Bookings</p>
                <p className="text-3xl font-bold">
                  {bookings.filter(b => 
                    new Date(b.created_at).toDateString() === new Date().toDateString()
                  ).length}
                </p>
              </div>
              <Calendar className="h-10 w-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by PNR, name, email, airline..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchBookings}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bookings List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-blue-500" />
            Bookings ({filteredBookings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500 mb-4" />
              <p className="text-gray-500">Loading bookings...</p>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Plane className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No bookings found</p>
              <p className="text-sm">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBookings.map((booking, index) => (
                <div 
                  key={booking.id || index}
                  className="border border-gray-200 rounded-xl overflow-hidden hover:border-blue-300 transition-colors"
                >
                  {/* Booking Header */}
                  <div 
                    className="p-4 bg-gray-50 cursor-pointer flex items-center justify-between"
                    onClick={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Plane className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg">{booking.pnr}</span>
                          <Badge variant={booking.status === 'cancelled' ? 'destructive' : 'default'} className="bg-green-100 text-green-700">
                            {booking.status || 'Confirmed'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">
                          {booking.flight_data?.from} → {booking.flight_data?.to} • {booking.flight_data?.airline}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="font-semibold text-lg">£{booking.flight_data?.price?.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">{formatDate(booking.created_at)}</p>
                      </div>
                      {expandedBooking === booking.id ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedBooking === booking.id && (
                    <div className="p-4 border-t border-gray-200 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Passenger Info */}
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Passengers
                          </h4>
                          {booking.passengers?.map((passenger, idx) => (
                            <div key={idx} className="mb-2 p-2 bg-gray-50 rounded">
                              <p className="font-medium">
                                {passenger.title} {passenger.first_name} {passenger.last_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {passenger.passenger_type} • DOB: {passenger.date_of_birth}
                              </p>
                            </div>
                          ))}
                        </div>

                        {/* Contact Info */}
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            Contact Details
                          </h4>
                          <div className="space-y-2 text-sm">
                            <p className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-gray-400" />
                              {booking.contact_email || '-'}
                            </p>
                            <p className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              {booking.contact_phone || '-'}
                            </p>
                          </div>
                        </div>

                        {/* Flight Info */}
                        <div>
                          <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Plane className="h-4 w-4" />
                            Flight Details
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="p-2 bg-blue-50 rounded">
                              <p className="font-medium text-blue-700">Outbound</p>
                              <p>{booking.flight_data?.flight_number} • {formatDate(booking.flight_data?.departure_date)}</p>
                              <p className="text-gray-600">
                                {formatTime(booking.flight_data?.departure_time)} → {formatTime(booking.flight_data?.arrival_time)}
                              </p>
                            </div>
                            {booking.flight_data?.return_departure_date && (
                              <div className="p-2 bg-green-50 rounded">
                                <p className="font-medium text-green-700">Return</p>
                                <p>{booking.flight_data?.return_flight_number} • {formatDate(booking.flight_data?.return_departure_date)}</p>
                                <p className="text-gray-600">
                                  {formatTime(booking.flight_data?.return_departure_time)} → {formatTime(booking.flight_data?.return_arrival_time)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateBookingPDF(booking)}
                          disabled={generatingPDF === booking.id}
                          className="flex items-center gap-2"
                        >
                          {generatingPDF === booking.id ? (
                            <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
                          ) : (
                            <><FileText className="h-4 w-4" /> Download PDF</>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingsPanel;
